import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ActivityKind } from "@visioni/a3s-activity-recorder/types";
import {
  type ActivityFilters,
  filtersToRouteParams,
  paramsToFilters,
  getDefaultActivityFilters,
  activityFilterSignature,
} from "../activity-filter-params";

const DEBOUNCE_MS = 400;

/** Current route/query params (e.g. from Expo Router's `useLocalSearchParams`). */
export type RouteParams = Record<string, string | string[] | undefined>;

/** Shallow-merges params into the route (e.g. Expo Router's `router.setParams`). */
export type SetRouteParams = (params: Record<string, string>) => void;

export interface UseActivityFiltersReturn {
  /** Immediate draft state for binding to inputs (no debounce lag). */
  filters: ActivityFilters;
  /** Committed filters derived from route params — use for queries. */
  committedFilters: ActivityFilters;
  updateFilter: <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => void;
  setFilters: (filters: ActivityFilters) => void;
  resetFilters: () => void;
  /** Convenience setter for the `kinds` multi-select. */
  setKinds: (kinds: readonly ActivityKind[]) => void;
}

/**
 * Unified activity filter hook. The host app supplies the current route
 * `params` and a `setParams` writer — the feature never imports a router
 * directly, so it stays out of the app's router dependency graph (which
 * otherwise causes a duplicate `expo-router` instance in the web bundle).
 *
 * - Exposes a `filters` draft that updates immediately for snappy UI.
 * - Debounces commits to route params (400ms) so queries and URL stay in sync.
 * - Uses `setParams` (not a full replace) to avoid remounting the component
 *   tree, which would close dropdowns and filter panels.
 * - Works identically on web (URL bar) and native (navigation params) — the
 *   host app decides how params are read and written.
 */
export function useActivityFilters(params: RouteParams, setParams: SetRouteParams): UseActivityFiltersReturn {
  // Keep a ref to the latest writer so committed callbacks stay stable while
  // always writing through the current router.
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  const lastCommittedSigRef = useRef<string | null>(null);

  const committedFilters = useMemo<ActivityFilters>(() => paramsToFilters(params), [params]);

  const [draft, setDraft] = useState<ActivityFilters>(committedFilters);

  const prevCommittedSigRef = useRef(activityFilterSignature(committedFilters));
  useEffect(() => {
    const sig = activityFilterSignature(committedFilters);
    if (sig === prevCommittedSigRef.current) return;
    prevCommittedSigRef.current = sig;

    if (sig === lastCommittedSigRef.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setDraft(committedFilters);
  }, [committedFilters]);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commitToRoute = useCallback((filters: ActivityFilters) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      lastCommittedSigRef.current = activityFilterSignature(filters);
      setParamsRef.current(filtersToRouteParams(filters));
    }, DEBOUNCE_MS);
  }, []);

  const updateFilter = useCallback(
    <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => {
      setDraft((prev) => {
        const next = { ...prev, [key]: value };
        commitToRoute(next);
        return next;
      });
    },
    [commitToRoute],
  );

  const setFilters = useCallback(
    (filters: ActivityFilters) => {
      setDraft(filters);
      commitToRoute(filters);
    },
    [commitToRoute],
  );

  const resetFilters = useCallback(() => {
    const defaults = getDefaultActivityFilters();
    setDraft(defaults);
    commitToRoute(defaults);
  }, [commitToRoute]);

  const setKinds = useCallback(
    (kinds: readonly ActivityKind[]) => {
      updateFilter("kinds", kinds);
    },
    [updateFilter],
  );

  return { filters: draft, committedFilters, updateFilter, setFilters, resetFilters, setKinds };
}
